import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ArrowLeftRight, Loader2, Pencil, Send, Trash2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Eyebrow, PageTitle, PageSubtitle, Loader, EmptyState, Card, StatusBadge, BackHeader, TripRoute } from "@/components/common";
import { PlaceSearch } from "@/components/PlaceSearch";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUSES = [
  ["Tutti", "all"],
  ["Bozze", "draft"],
  ["In attesa", "pending"],
  ["Assegnati", "confirmed"],
  ["In corso", "in_progress"],
  ["Completati", "completed"],
  ["Ceduti", "transferred"],
];
const LABELS = {
  draft: "Bozza",
  pending: "In attesa di assegnazione",
  confirmed: "Assegnato",
  in_progress: "In corso",
  completed: "Completato",
  transferred: "Ceduto",
  cancelled: "Annullato",
  scaduto: "Scaduto",
  available: "Disponibile",
};
const TRANSFERABLE = ["pending", "confirmed", "in_progress", "available"];

export default function TransportsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);
  const [transferTrip, setTransferTrip] = useState(null);
  const [transferPrice, setTransferPrice] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [transferError, setTransferError] = useState("");

  const load = () => {
    setLoading(true);
    apiFetch("/transports").then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const openTransfer = (item) => {
    setTransferTrip(item);
    setTransferPrice("");
    setTransferNotes("");
    setTransferError("");
  };

  const submitTransfer = async () => {
    const value = Number(transferPrice);
    if (!transferTrip || !value || value <= 0) {
      setTransferError("Inserisci il prezzo da proporre al vettore.");
      return;
    }
    setSaving(true);
    setTransferError("");
    try {
      await apiFetch(`/trips/${transferTrip.id}/transfer`, {
        method: "POST",
        body: JSON.stringify({ price_proposed: value, notes: transferNotes }),
      });
      setTransferTrip(null);
      load();
    } catch (error) {
      setTransferError(error.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const canTransfer = user?.account_type === "vettore";

  const [editTrip, setEditTrip] = useState(null);
  const [editForm, setEditForm] = useState({ pickup: "", delivery: "", date: "" });
  const [editPickupPlace, setEditPickupPlace] = useState(null);
  const [editDeliveryPlace, setEditDeliveryPlace] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [publishingId, setPublishingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const EDITABLE = ["draft", "pending", "available"];
  const deleteTrip = async (item) => {
    if (!window.confirm("Eliminare definitivamente questo viaggio? L'operazione non è reversibile.")) return;
    setDeletingId(item.id);
    try {
      await apiFetch(`/trips/${item.id}`, { method: "DELETE" });
      load();
    } catch (error) {
      window.alert(error.message || "Impossibile eliminare il viaggio.");
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (item) => {
    setEditTrip(item);
    setEditForm({ pickup: item.pickup || "", delivery: item.delivery || "", date: item.date || "" });
    setEditPickupPlace(item.pickup_place || null);
    setEditDeliveryPlace(item.delivery_place || null);
    setEditError("");
  };

  const saveEdit = async () => {
    if (!editTrip) return;
    if (!editForm.pickup.trim() || !editForm.delivery.trim()) {
      setEditError("Carico e scarico sono obbligatori.");
      return;
    }
    setEditSaving(true);
    setEditError("");
    try {
      await apiFetch(`/trips/${editTrip.id}`, {
        method: "PUT",
        body: JSON.stringify({
          pickup: editForm.pickup, delivery: editForm.delivery, date: editForm.date,
          pickup_place: editPickupPlace, delivery_place: editDeliveryPlace,
        }),
      });
      setEditTrip(null);
      load();
    } catch (error) {
      setEditError(error.message || "Riprova.");
    } finally {
      setEditSaving(false);
    }
  };

  const publishDraft = async (item) => {
    setPublishingId(item.id);
    try {
      await apiFetch(`/trips/${item.id}/publish`, { method: "PATCH" });
      load();
    } catch (error) {
      setEditTrip(item);
      setEditForm({ pickup: item.pickup || "", delivery: item.delivery || "", date: item.date || "" });
      setEditPickupPlace(item.pickup_place || null);
      setEditDeliveryPlace(item.delivery_place || null);
      setEditError(error.message || "Completa i dati essenziali prima di pubblicare.");
    } finally {
      setPublishingId(null);
    }
  };

  const filtered = useMemo(
    () => (active === "all" ? items : items.filter((i) => i.status === active)),
    [items, active]
  );

  return (
    <div className="space-y-3">
      <BackHeader />
      <Eyebrow>CONTROLLO</Eyebrow>
      <PageTitle>I miei trasporti</PageTitle>
      <PageSubtitle>Segui ogni incarico, dall&apos;assegnazione alla consegna.</PageSubtitle>

      <div className="flex gap-2 overflow-x-auto py-1">
        {STATUSES.map(([label, value]) => (
          <button
            key={value}
            data-testid={`transport-filter-${value}`}
            onClick={() => setActive(value)}
            className={cn(
              "shrink-0 rounded-full px-3.5 py-2 text-xs font-bold transition-colors",
              active === value ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : filtered.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nessun trasporto qui" text="I viaggi pubblicati o confermati compariranno in questa sezione." />
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <Card key={item.id} data-testid={`transport-card-${item.id}`} className="space-y-3">
              <Link to={`/trip/${item.id}`} className="block space-y-2">
                <TripRoute trip={item} />
                <p className="text-[13px] text-muted-foreground">{item.date} · {item.vehicle_type}</p>
                {item.assigned_to_name ? <p data-testid={`transport-carrier-${item.id}`} className="text-[13px] font-bold text-card-foreground">Vettore: {item.assigned_to_name}</p> : null}
              </Link>
              <div className="flex items-center justify-between gap-2">
                <StatusBadge tone={item.status === "completed" ? "success" : item.status === "draft" ? "warning" : "neutral"}>
                  {LABELS[item.status] || item.status}
                </StatusBadge>
                <div className="flex items-center gap-3">
                  {EDITABLE.includes(item.status) ? (
                    <button
                      data-testid={`draft-edit-button-${item.id}`}
                      onClick={() => openEdit(item)}
                      className="flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-extrabold text-foreground hover:bg-secondary"
                    >
                      <Pencil className="h-4 w-4" /> Modifica
                    </button>
                  ) : null}
                  {item.status === "draft" ? (
                    <button
                      data-testid={`draft-publish-button-${item.id}`}
                      onClick={() => publishDraft(item)}
                      disabled={publishingId === item.id}
                      className="flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-extrabold text-primary-foreground disabled:opacity-60"
                    >
                      {publishingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Pubblica
                    </button>
                  ) : null}
                  {EDITABLE.includes(item.status) ? (
                    <button
                      data-testid={`trip-delete-button-${item.id}`}
                      onClick={() => deleteTrip(item)}
                      disabled={deletingId === item.id}
                      className="flex items-center gap-1.5 rounded-full border border-destructive px-3 py-1.5 text-xs font-extrabold text-destructive hover:bg-destructive/10 disabled:opacity-60"
                    >
                      {deletingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Elimina
                    </button>
                  ) : null}
                  {canTransfer && TRANSFERABLE.includes(item.status) ? (
                    <button
                      data-testid={`transfer-open-button-${item.id}`}
                      onClick={() => openTransfer(item)}
                      className="flex items-center gap-1.5 rounded-full border border-primary px-3 py-1.5 text-xs font-extrabold text-primary hover:bg-secondary"
                    >
                      <ArrowLeftRight className="h-4 w-4" /> Cedi a vettore
                    </button>
                  ) : null}
                  {item.price_proposed ? <span className="font-extrabold text-primary">€ {item.price_proposed}</span> : null}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={transferTrip !== null} onOpenChange={(o) => !o && setTransferTrip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cedi viaggio a un vettore</DialogTitle>
          </DialogHeader>
          {transferTrip ? (
            <p className="text-sm font-extrabold text-primary">
              {transferTrip.pickup} → {transferTrip.delivery} · {transferTrip.date}
            </p>
          ) : null}
          <p className="text-[13px] leading-snug text-muted-foreground">
            Il vettore vedrà solo il prezzo che proponi qui: il prezzo originale del committente resta privato.
          </p>
          <label className="mt-2 text-xs font-bold text-card-foreground">Prezzo proposto al vettore (€)</label>
          <input
            data-testid="transfer-price-input"
            value={transferPrice}
            onChange={(e) => setTransferPrice(e.target.value)}
            placeholder="Es. 450"
            inputMode="numeric"
            className="min-h-[48px] rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
          />
          <label className="mt-1 text-xs font-bold text-card-foreground">Note per il vettore</label>
          <input
            data-testid="transfer-notes-input"
            value={transferNotes}
            onChange={(e) => setTransferNotes(e.target.value)}
            placeholder="Istruzioni aggiuntive"
            className="min-h-[48px] rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
          />
          {transferError ? <p data-testid="transfer-error-text" className="text-sm text-destructive">{transferError}</p> : null}
          <button
            data-testid="transfer-submit-button"
            onClick={submitTransfer}
            disabled={saving}
            className="mt-3 flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            PUBBLICA CESSIONE
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={editTrip !== null} onOpenChange={(o) => !o && setEditTrip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editTrip?.status === "draft" ? "Completa la bozza" : "Modifica viaggio"}</DialogTitle>
          </DialogHeader>
          <label className="mt-1 text-xs font-bold text-card-foreground">Luogo di carico</label>
          <PlaceSearch testId="edit-field-pickup" value={editForm.pickup} onChange={(v) => setEditForm((f) => ({ ...f, pickup: v }))} onSelectPlace={setEditPickupPlace} selected={editPickupPlace} />
          <label className="mt-2 text-xs font-bold text-card-foreground">Luogo di scarico</label>
          <PlaceSearch testId="edit-field-delivery" value={editForm.delivery} onChange={(v) => setEditForm((f) => ({ ...f, delivery: v }))} onSelectPlace={setEditDeliveryPlace} selected={editDeliveryPlace} />
          <label className="mt-2 text-xs font-bold text-card-foreground">Data di carico</label>
          <input
            type="date"
            data-testid="edit-field-date"
            value={editForm.date}
            onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
            className="min-h-[48px] rounded-xl border border-border bg-secondary/40 px-3 text-[15px] outline-none focus:border-primary"
          />
          {editError ? <p data-testid="edit-error-text" className="text-sm text-destructive">{editError}</p> : null}
          <button
            data-testid="edit-save-button"
            onClick={saveEdit}
            disabled={editSaving}
            className="mt-3 flex min-h-[52px] items-center justify-center gap-2 rounded-2xl bg-primary text-sm font-extrabold text-primary-foreground disabled:opacity-60"
          >
            {editSaving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
            SALVA MODIFICHE
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
